// The task's "History" section (Carla, 2026-10-21): "Moved 3 times. Originally due 2 Oct", then each
// change, newest first, with its reason and who made it. The server decides who may read it (the
// person whose task it is; the business's owner and admins for the team's), and answers 404 to
// anyone else, which shows nothing here rather than an error about a task they cannot see.
import React, { useEffect, useState } from 'react';
import { fetchDueHistory, historySummary, dueDay, seriesIdOf } from '../../utils/taskDueChanges';
import { formatTaskDay } from '../../utils/taskTime';
import { messageFromError } from '../../utils/apiError';
import './overdue.css';

const SOURCE_LABEL = {
  popup: 'from the overdue list', bulk: 'with other tasks from the overdue list', task_edit: 'in the task', calendar_drag: 'on the calendar',
  aidey_chat: 'by asking Aidey', api: null, workflow: 'by a workflow', booking_reschedule: 'by a booking reschedule',
  calendar_sync: 'by your calendar', event_suggestion: 'to make room for an event', other: null,
};

export default function TaskDueHistory({ task }) {
  const id = seriesIdOf(task);
  const occurrence = task?.isRecurringInstance ? task.occurrenceStart : null;
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    if (!id || task?.isBirthday || !/^\d+$/.test(id)) { setState({ hidden: true }); return undefined; }
    setState({ loading: true });
    fetchDueHistory(id, occurrence)
      // An answer without its history is not an empty history: said as not loaded.
      .then((d) => { if (live) setState(Array.isArray(d?.history) ? { data: d } : { error: 'This task\'s history could not be loaded.' }); })
      .catch((e) => { if (live) setState(e?.status === 404 ? { hidden: true } : { error: messageFromError(e, 'This task\'s history could not be loaded.') }); });
    return () => { live = false; };
  }, [id, occurrence, task?.isBirthday, task?.deadline, task?.datetime, task?.date]);

  if (state.hidden) return null;
  return (
    <section className="ov-history" aria-labelledby={`hist-${id}`} data-testid="task-history">
      <h4 id={`hist-${id}`} className="ov-history__title">History</h4>
      {state.loading && <p className="tk-small">Loading…</p>}
      {state.error && <p className="tk-small" role="alert">{state.error}</p>}
      {state.data && (
        <>
          <p className="ov-history__summary" data-testid="task-history-summary">{historySummary(state.data)}</p>
          {state.data.history.length > 0 && (
            <ol className="ov-history__list">
              {state.data.history.map((h) => {
                const via = SOURCE_LABEL[h.source];
                const who = h.by?.name || null;
                return (
                  <li key={h.id} className="ov-history__item">
                    <span className="ov-history__move">{h.oldDue ? dueDay(h.oldDue) : 'No date'} → {h.newDue ? dueDay(h.newDue) : 'No date'}</span>
                    {h.reasonLabel && <span className="ov-history__reason">{h.reasonLabel}{h.note ? `: ${h.note}` : ''}</span>}
                    <span className="tk-small">
                      {[who ? `By ${who}` : null, via, formatTaskDay(new Date(h.at))].filter(Boolean).join(', ')}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </>
      )}
    </section>
  );
}
